import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { SmilePlus, Search } from 'lucide-react';
import { EMOJI_CATEGORIES, SKIN_TONES, getEmojiWithSkinTone } from '@/lib/emojis';
import type { BaseEmoji } from '@/lib/emojis';

const CATEGORY_ICONS = ['😀', '👋', '🐶', '🍔', '🚀', '⚽', '💡', '🔣', '🏁'];
const PICKER_WIDTH = 352;
const PICKER_HEIGHT = 420;

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
}

export default function EmojiPicker({ onSelect }: EmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [skinTone, setSkinTone] = useState('');
  const [showSkinTones, setShowSkinTones] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    let top = rect.top - PICKER_HEIGHT - 8;
    let left = rect.left;
    if (top < 8) top = rect.bottom + 8;
    if (left + PICKER_WIDTH > window.innerWidth - 8) left = window.innerWidth - PICKER_WIDTH - 8;
    if (left < 8) left = 8;
    setPos({ top, left });
  }, []);

  useEffect(() => {
    if (!open) return;
    computePosition();
    function handleClickOutside(e: MouseEvent) {
      if (
        pickerRef.current && !pickerRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setShowSkinTones(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', computePosition);
    window.addEventListener('scroll', computePosition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', computePosition);
      window.removeEventListener('scroll', computePosition, true);
    };
  }, [open, computePosition]);

  const isSearching = search.trim().length > 0;
  const searchLower = search.toLowerCase();

  const searchResults = isSearching
    ? EMOJI_CATEGORIES.flatMap(cat =>
        cat.emojis.filter(
          (e: BaseEmoji) =>
            e.description.toLowerCase().includes(searchLower) ||
            e.keywords.some((k: string) => k.toLowerCase().includes(searchLower))
        )
      )
    : [];

  const activeEmojis = isSearching ? searchResults : EMOJI_CATEGORIES[activeCategory]?.emojis || [];

  const handleSelect = (emoji: BaseEmoji) => {
    const resolved = getEmojiWithSkinTone(emoji, skinTone);
    onSelect(resolved);
    setOpen(false);
    setSearch('');
  };

  const handleOpen = () => {
    if (!open) {
      setSearch('');
      setActiveCategory(0);
      setShowSkinTones(false);
    }
    setOpen(!open);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-500 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:text-gray-300 transition-colors"
        title="Add reaction"
      >
        <SmilePlus className="h-3.5 w-3.5" />
      </button>
      {open && createPortal(
        <div
          ref={pickerRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: PICKER_WIDTH, zIndex: 9999 }}
          className="rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800 flex flex-col"
        >
          {/* Search + skin tone */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
            <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search emojis..."
              className="flex-1 text-sm bg-transparent outline-none text-gray-900 dark:text-white placeholder:text-gray-400"
              autoFocus
            />
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowSkinTones(!showSkinTones)}
                className="flex h-6 w-6 items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-sm"
                title="Skin tone"
              >
                {skinTone ? `👋${skinTone}` : '👋'}
              </button>
              {showSkinTones && (
                <div className="absolute top-full right-0 mt-1 flex gap-0.5 rounded-md border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800 z-20">
                  {SKIN_TONES.map(st => (
                    <button
                      key={st.label}
                      type="button"
                      onClick={() => {
                        setSkinTone(st.emoji);
                        setShowSkinTones(false);
                      }}
                      className={`flex h-7 w-7 items-center justify-center rounded text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        skinTone === st.emoji ? 'bg-brand-100 dark:bg-brand-900/30' : ''
                      }`}
                      title={st.label}
                    >
                      {st.emoji ? `✋${st.emoji}` : '✋'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Category tabs — hidden during search */}
          {!isSearching && (
            <div className="flex items-center border-b border-gray-100 dark:border-gray-700/50 px-1">
              {EMOJI_CATEGORIES.map((cat, i) => (
                <button
                  key={cat.label}
                  type="button"
                  onClick={() => {
                    setActiveCategory(i);
                    gridRef.current?.scrollTo({ top: 0 });
                  }}
                  className={`flex-1 flex items-center justify-center py-1.5 text-base transition-colors border-b-2 ${
                    activeCategory === i
                      ? 'border-brand-500 bg-brand-50/50 dark:bg-brand-950/20'
                      : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                  title={cat.label}
                >
                  {CATEGORY_ICONS[i] || '⬜'}
                </button>
              ))}
            </div>
          )}

          {/* Category label */}
          {!isSearching && (
            <div className="px-3 pt-2 pb-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                {EMOJI_CATEGORIES[activeCategory]?.label}
              </span>
            </div>
          )}
          {isSearching && searchResults.length > 0 && (
            <div className="px-3 pt-2 pb-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                Results ({searchResults.length})
              </span>
            </div>
          )}

          {/* Emoji grid */}
          <div ref={gridRef} className="flex-1 min-h-0 overflow-y-auto px-2 pb-2" style={{ maxHeight: 350 }}>
            {activeEmojis.length === 0 ? (
              <div className="flex items-center justify-center h-full text-sm text-gray-400">
                No emojis found
              </div>
            ) : (
              <div className="grid grid-cols-8 gap-0.5">
                {activeEmojis.map((e: BaseEmoji) => (
                  <button
                    key={e.emoji}
                    type="button"
                    onClick={() => handleSelect(e)}
                    className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-xl transition-colors"
                    title={e.description}
                  >
                    {getEmojiWithSkinTone(e, skinTone)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
